
import React, { useState, useEffect, useContext } from "react";
import Footer from "../components/dashboard/footer";
import Header from "../components/dashboard/header";
import Sidebar from "../components/dashboard/sidebar";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import moment from "moment";
import { contentContext } from "../context/contentContext";
import SessionPopup from "./SessionPopup";
import BankConnectBanner from "./dashboard/BankConnectBanner";
import TableLoader from "../components/tableLoader";
import DataTable from 'react-data-table-component';

let { lambda, appname } = window.app;

const Payouts = () => {
    const navigate = useNavigate();

    const [showSessionPopupup, setShowSessionPopupup] = useState(false);
    const [payoutsData, setPayoutsData] = useState(null);
    const [payoutsLoading, setPayoutsLoading] = useState(false);
    const [payoutsErrMsg, setPayoutsErrMsg] = useState("");
    // "No Stripe account yet" is the normal state before onboarding, not a
    // failure - the page stays intact and only the empty-table copy changes.
    const [notConnected, setNotConnected] = useState(false);

    const { clientInfo, setActiveMenuId, setActiveMenuObj, setRoute, GetTimeActivity } = useContext(contentContext);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { navigate("/signin"); return; }
        if (!clientInfo?.subscriptionPayment) return;
        if (clientInfo.subscriptionPayment !== "PAYMENT SUCCESS") navigate("/signin");
    }, [clientInfo, navigate]);

    useEffect(() => {
        setRoute("payouts");
        setActiveMenuId();
        setActiveMenuObj({ "Client Management": false, "Contact Management": false, "Reports": false });
        userActivity();
        fetchPayouts();
    }, []);

    const userActivity = () => {
        const pageName = window.location.pathname.split("/").pop();
        const presentTime = moment();
        const payload = {
            clientid: localStorage.getItem("clientid"),
            pagename: pageName,
            pageurl: window.location.href,
            starttime: presentTime,
            useragent: JSON.parse(localStorage.getItem("loc")),
        };
        const previousId = localStorage.getItem("previousid");
        const urlLink = lambda + '/useractivity?appname=' + appname + (previousId ? "&previousid=" + previousId : "");
        axios({ method: 'POST', url: urlLink, data: payload })
            .then(response => {
                if (response?.data?.statusCode === 200) localStorage.setItem("previousid", response?.data?.result);
            });
    };

    // Reads live from Stripe (via the backend's getStripePayouts) - not our own DB -
    // so this is a real network round-trip each time the page loads, not an instant
    // read from cached state.
    const fetchPayouts = () => {
        setPayoutsLoading(true);
        setPayoutsErrMsg("");
        setNotConnected(false);
        const token = localStorage.getItem("token");
        axios({
            method: 'GET',
            // No clientid: the backend scopes this to whoever the token belongs to.
            url: `${lambda}/stripeConnectPayouts?appname=${appname}&token=${token}`,
        })
            .then(function (response) {
                const result = response?.data?.result;
                if (result === "Invalid token or Expired") {
                    setShowSessionPopupup(true);
                } else if (result?.error) {
                    // Backend error strings are written for developers (they name
                    // internal handlers), so never render one verbatim to a client.
                    setPayoutsData(null);
                    if (/no stripe account/i.test(result.message || "")) {
                        setNotConnected(true);
                    } else {
                        setPayoutsErrMsg("We couldn't load your payouts just now. Please refresh in a moment.");
                    }
                } else {
                    setPayoutsData(result);
                }
            })
            .catch((err) => {
                console.log('error fetching payouts', err);
                setPayoutsData(null);
                setPayoutsErrMsg("Something went wrong loading payouts. Please try again.");
            })
            .finally(() => setPayoutsLoading(false));
    };

    // ── Formatting helpers ───────────────────────────────────────────
    const formatMoney = (amount, currency = 'USD') => {
        if (amount == null || isNaN(amount)) return '—';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase() }).format(amount);
    };

    const getPayoutStatusBadge = (status) => {
        const map = {
            paid: { bg: '#d1e7dd', color: '#0f5132', label: '✓ Paid' },
            pending: { bg: '#fff3cd', color: '#664d03', label: 'Pending' },
            in_transit: { bg: '#cfe2ff', color: '#084298', label: 'In Transit' },
            canceled: { bg: '#e2e3e5', color: '#41464b', label: 'Canceled' },
            failed: { bg: '#f8d7da', color: '#842029', label: '✕ Failed' },
        };
        const s = map[status] || { bg: '#e2e3e5', color: '#41464b', label: status || '—' };
        return <span style={{ background: s.bg, color: s.color, borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>;
    };

    const payoutColumns = [
        { name: '#', cell: (row, index) => index + 1, width: '5%' },
        {
            name: 'Payout Date',
            selector: row => row?.createdDate ?? "",
            cell: (row) => (
                <div>
                    <div>{moment(row.createdDate).format('MM/DD/YYYY')}</div>
                    <div className="text-muted" style={{ fontSize: '12px' }}>{moment(row.createdDate).format('h:mm A')}</div>
                </div>
            ),
            sortable: true,
        },
        {
            name: 'Bank',
            selector: row => row?.bankName ?? "",
            cell: (row) => (
                <span>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>account_balance</span>
                    {row.bankName || '—'}{row.last4 ? ` ••${row.last4}` : ''}
                </span>
            ),
        },
        {
            name: 'Amount',
            selector: row => row?.amount ?? 0,
            cell: (row) => <span style={{ color: '#198754', fontWeight: 600 }}>{formatMoney(row.amount, row.currency)}</span>,
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row?.status ?? "",
            cell: (row) => <span title={row.status === 'failed' ? row.failureMessage : undefined}>{getPayoutStatusBadge(row.status)}</span>,
        },
        {
            name: 'Expected In Bank',
            selector: row => row?.arrivalDate ?? "",
            cell: (row) => row.arrivalDate ? moment(row.arrivalDate).format('MM/DD/YYYY') : '—',
        },
    ];

    // Before the bank is connected there is nothing to find rather than nothing
    // there - the banner above already carries the fix, so this only says why the
    // table is empty instead of repeating the call to action.
    const customNoPayouts = () => (
        <div className="empty-state-body empty-record">
            <div className="empty-state__message">
                <span className="material-icons">summarize</span>
                <p className="form-check font-size-16">
                    {notConnected
                        ? "Your payouts will appear here once your bank account is connected."
                        : "No payouts were found"}
                </p>
            </div>
        </div>
    );

    return (
        <>
            {showSessionPopupup && <SessionPopup />}

            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content user-management transaction-page-table" id="payouts-main-content">
                    <div className="page-content" id="payouts-page-content">
                        <div className="container-fluid" id="payouts-container-fluid">

                            <div className="row mb-4 breadcrumb" id="payouts-breadcrumb-row">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title" id="payouts-breadcrumb-title">Payouts</h4>
                                        </div>
                                    </div>
                                </div>
                                {/* Above the payouts branch, not inside it: with no bank connected
                                    Stripe returns nothing and this page falls to the error or empty
                                    state, which explains neither the cause nor the fix. */}
                                <BankConnectBanner />
                            </div>

                            {payoutsErrMsg ? (
                                <div className="card">
                                    <div className="card-body text-center text-muted">{payoutsErrMsg}</div>
                                </div>
                            ) : payoutsLoading ? (
                                <TableLoader />
                            ) : (
                                <>
                                    <div className="row g-3 mb-4" id="payouts-summary-cards">
                                        <div className="col-md-6">
                                            <div className="card dashboard-box features-box" id="payouts-pending-card">
                                                <div className="card-body">
                                                    <div className="d-flex">
                                                        <div className="flex-grow-1">
                                                            <p className="feu-txt-18">Pending</p>
                                                            <h4 className="feu-txt-20">
                                                                {formatMoney((payoutsData?.balance?.available ?? 0) + (payoutsData?.balance?.pending ?? 0), payoutsData?.payouts?.[0]?.currency)}
                                                            </h4>
                                                            <small className="text-muted">On the way to you — Stripe holds each payment 2 days before it can be sent, then deposited every day</small>
                                                        </div>
                                                        <div className="flex-shrink-0 align-self-center">
                                                            <div className="feu-icon-section">
                                                                <span className="material-symbols-outlined">schedule</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card dashboard-box features-box" id="payouts-total-paid-card">
                                                <div className="card-body">
                                                    <div className="d-flex">
                                                        <div className="flex-grow-1">
                                                            <p className="feu-txt-18">Total Paid Out</p>
                                                            <h4 className="feu-txt-20">
                                                                {formatMoney(payoutsData?.totalPaidOut, payoutsData?.payouts?.[0]?.currency)}
                                                            </h4>
                                                            <small className="text-muted">Deposited to your bank to date</small>
                                                        </div>
                                                        <div className="flex-shrink-0 align-self-center">
                                                            <div className="feu-icon-section">
                                                                <span className="material-symbols-outlined">account_balance</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row table-data" id="payouts-table-data-row">
                                        <div className="col-12">
                                            <div className="card" id="payouts-card">
                                                <div className="card-body" id="payouts-card-body">
                                                    <DataTable
                                                        columns={payoutColumns}
                                                        keyField="payoutId"
                                                        data={payoutsData?.payouts || []}
                                                        direction="auto"
                                                        highlightOnHover
                                                        pagination
                                                        responsive
                                                        striped
                                                        noDataComponent={customNoPayouts()}
                                                        id="payouts-data-table"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                        </div>
                    </div>

                    <Footer id="payouts-footer" />
                </div>
            </div>
        </>
    );
};

export default Payouts;
